import { type RouteLocationNormalized, type RouteLocation, useRouter } from 'vue-router'
import { useContextStore } from '@/store/ContextStore'
import { useLoginStore } from '@/store/LoginStore'
import { useNavStore } from '@/store/NavStore'
import { useProductStore } from '@/store/ProductStore'
import { useAuthStore } from '@/store/AuthStore'
import { useFeatureFlagStore } from '@/store/FeatureFlagStore'
import { storeToRefs } from 'pinia'
import useRefreshAuth from '@/composables/refreshAuth'
import { ViewName } from '@/types'
import { usePasswordResetStore } from '@/store/PasswordResetStore'

const validHosts = [
  import.meta.env.VITE_ACCOUNT_HOSTNAME,
  import.meta.env.VITE_LEGACY_SHOP_HOSTNAME,
  import.meta.env.VITE_SUBSCRIPTIONS_HOSTNAME,
  import.meta.env.VITE_COPRODUCER_HOSTNAME,
  import.meta.env.VITE_SITE_PAGES_HOSTNAME,
  import.meta.env.VITE_PRODUCT_PAGES_HOSTNAME
]

export const setFeatureFlags = () => {
  const { fetchFlags } = useFeatureFlagStore()
  fetchFlags()
}

export const fetchFooterData = async () => {
  const navStore = useNavStore()
  navStore.fetchFooterContent()
}

export const removeReferrer = (to: RouteLocation) => {
  const { query: toQuery, path } = to
  if (toQuery.referrer) {
    const { referrer, ...query } = toQuery

    return { path, query }
  }
}

export const sendRouteUpdate = (route: RouteLocation) => {
  const loginStore = useLoginStore()
  const { params, query } = storeToRefs(loginStore)

  const detail = {
    ...route,
    query: query.value,
    params: params.value
  }

  const evt = new CustomEvent('remoteSuccess', { detail })
  document.dispatchEvent(evt)
}

export const handleRemote = (to: RouteLocationNormalized) => {
  const { meta } = to

  // send route updates to remote exposed routes only
  if (meta.isLoginServiceRoute) {
    sendRouteUpdate(to)
  }
}

export const handleHostRedirect = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  const { query: toQuery } = to
  const { name: fromName } = from
  const { path, host, ...query } = toQuery

  const currentHost = `${window.location.protocol}//${window.location.host}`

  // Redirect when host or path is sent
  if (host || path) {
    const hostName = host ? decodeURI(host as string) : ''
    const returnPath = path ? decodeURI(path as string) : ''

    const redirectHost = hostName && validHosts.includes(hostName) ? hostName : currentHost

    query.from = fromName as string
    // build return URL
    const returnUrl = `${redirectHost}${returnPath}`
    const isQuery = Object.entries(query).length !== 0
    const queryString = Object.keys(query)
      .map(key => `${key}=${query[key]}`)
      .join('&')
    const redirectUrl = isQuery ? `${returnUrl}?${queryString}` : `${returnUrl}`

    window.location.replace(redirectUrl)
  }
}

export const setProduct = (to: RouteLocationNormalized) => {
  const { params } = to
  const { setProduct: setStoreProduct } = useProductStore()

  if (params.productName) {
    setStoreProduct(params.productName as string)
  }
}

export const setViewName = (to: RouteLocationNormalized) => {
  const { meta } = to
  const viewContextName = meta?.viewContextName as ViewName

  const { setViewName: setStoreViewName } = useContextStore()

  setStoreViewName(viewContextName)
}

export const setAppReferrer = (to: RouteLocationNormalized) => {
  const { query: toQuery } = to
  const { appReferrer } = toQuery

  const { setAppReferrer } = useLoginStore()

  if (appReferrer) {
    const appReferrerKey = appReferrer as string
    setAppReferrer(appReferrerKey)
  }
}

export const setChallengeId = (to: RouteLocationNormalized) => {
  const { setChallengeId: setStoreId } = useAuthStore()
  const { query: toQuery, path } = to
  const router = useRouter()
  const { challenge, ...query } = toQuery

  if (challenge) {
    const challengeId = challenge as string
    setStoreId(challengeId)

    router.replace({ path, query })
  }
}

export const redirectToRefresh = (to: RouteLocationNormalized) => {
  const { params, query } = to
  const authStore = useAuthStore()
  const { isUserAuthenticated } = storeToRefs(authStore)

  if (isUserAuthenticated.value) {
    return { name: 'refresh', params, query }
  }
}

export const handleRefresh = async (to: RouteLocationNormalized) => {
  const { postRefresh } = useRefreshAuth()
  const { query, params } = to

  try {
    const payload = await postRefresh()
    const accessToken = payload?.data?.refresh?.accessToken
    // refresh failed (e.g. a stale JWT with no/expired refresh token) — bail to logout
    if (!accessToken) {
      return { name: 'logout', query, params }
    }

    return { name: 'success', query, params }
  } catch {
    // the refresh mutation itself rejected — same graceful exit
    return { name: 'logout', query, params }
  }
}

export const handlePasswordResetCode = async (to: RouteLocationNormalized) => {
  const { query: toQuery, path } = to
  const { handleResetCode } = usePasswordResetStore()
  const router = useRouter()

  const { resetCode: code, ...query } = toQuery

  if (code) {
    const resetCode = code as string
    await handleResetCode(resetCode)

    router.replace({ path, query })
  }
}

export const canResetPassword = () => {
  const passwordResetStore = usePasswordResetStore()
  const { resetCode } = storeToRefs(passwordResetStore)

  if (!resetCode.value) {
    return { name: 'signIn' }
  }
}
